import { useMemo, useState } from "react";
import { Alert, Box, Button } from "@mui/material";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";

import { StatusChip } from "./StatusChip";
import { DiffCell } from "./DiffCell";
import FlagIcon from "@mui/icons-material/Flag";
import { indexCol } from "./indexCol";

const COLUMN_FIELDS = [
  //   { accessor: "item", header: "#", size: 20 },
  { accessor: "uid", header: "UID", size: 50 },
  { accessor: "tagNumber", header: "Tag #/Mark #", size: 50 },
  { accessor: "description", header: "Description", size: 100 },
  { accessor: "quantity", header: "Qty", size: 20 },
  { accessor: "length", header: "Dimension", size: 20 },
  // { accessor: "cost", header: "Cost", size: 20 },
  // { accessor: "hrs", header: "hr", size: 20 },
];

function buildBomCols(side, color) {
  return COLUMN_FIELDS.map(({ accessor, header, size }) => ({
    id: `${side}_${accessor}`,
    header,
    accessorFn: (row) => row?.[side]?.[accessor] ?? "",
    size,

    muiTableHeadCellProps: {
      align: accessor === "description" ? "left" : "center",
      sx: {
        fontWeight: 700,
        color,
        border: "1px solid rgba(0,0,0,0.12)",
        fontSize: "0.75rem",
      },
    },

    muiTableBodyCellProps: {
      align: accessor === "description" ? "left" : "center",
      sx: {
        border: "1px solid rgba(0,0,0,0.12)",
        fontSize: "0.78rem",
        py: 0.7,
        verticalAlign: "top",
      },
    },

    Cell: ({ row }) => {
      const { diffs, status, left, right } = row.original;

      const missingL = status === "right-only";
      const missingR = status === "left-only";

      const isLeft = side === "left";
      const src = isLeft ? left : right;

      return (
        <Box
          sx={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: 1.35,
            opacity: isLeft ? (missingL ? 0.45 : 1) : missingR ? 0.45 : 1,
          }}
        >
          {accessor === "tagNumber"
            ? src?.tagNumbers?.join(", ")
            : src?.[accessor]}
        </Box>
      );
    },
  }));
}

export function ComparisonTable({
  rows,
  filter,
  leftSideLabel,
  rightSideLabel,
  selectedRows,
  setSelectedRows,
}) {
  console.log("selectedRows", selectedRows);
  console.log("rows", rows);

  const onClickReviseMRQ = (row) => {
    console.log("Revise MRQ for:", row);
  };

  const data = useMemo(
    () => (filter === "all" ? rows : rows.filter((r) => r.status === filter)),
    [rows, filter],
  );

  const GROUP_BORDER = "1px solid rgba(0,0,0,0.9)";

  const columns = useMemo(
    () => [
      indexCol,
      {
        id: "left_group",
        header: `${leftSideLabel} BOM`,

        muiTableHeadCellProps: {
          sx: {
            borderRight: GROUP_BORDER,
            borderLeft: GROUP_BORDER,
            borderTop: GROUP_BORDER,
            borderBottom: GROUP_BORDER,
          },
        },

        columns: buildBomCols("left", "primary.main").map(
          (col, index, arr) => ({
            ...col,

            muiTableHeadCellProps: {
              ...col.muiTableHeadCellProps,

              sx: {
                ...col.muiTableHeadCellProps?.sx,

                borderLeft:
                  index === 0 ? GROUP_BORDER : "1px solid rgba(0,0,0,0.12)",

                borderRight:
                  index === arr.length - 1
                    ? GROUP_BORDER
                    : "1px solid rgba(0,0,0,0.12)",
              },
            },

            muiTableBodyCellProps: {
              ...col.muiTableBodyCellProps,

              sx: {
                ...col.muiTableBodyCellProps?.sx,

                borderLeft:
                  index === 0 ? GROUP_BORDER : "1px solid rgba(0,0,0,0.12)",

                borderRight:
                  index === arr.length - 1
                    ? GROUP_BORDER
                    : "1px solid rgba(0,0,0,0.12)",
              },
            },
          }),
        ),
      },

      {
        id: "right_group",
        header: `${rightSideLabel} BOM`,

        muiTableHeadCellProps: {
          sx: {
            borderRight: GROUP_BORDER,
            borderTop: GROUP_BORDER,
            borderBottom: GROUP_BORDER,
          },
        },

        columns: buildBomCols("right", "secondary.main").map(
          (col, index, arr) => ({
            ...col,

            muiTableHeadCellProps: {
              ...col.muiTableHeadCellProps,

              sx: {
                ...col.muiTableHeadCellProps?.sx,

                // borderLeft:
                //   index === 0 ? GROUP_BORDER : "1px solid rgba(0,0,0,0.12)",

                borderRight:
                  index === arr.length - 1
                    ? GROUP_BORDER
                    : "1px solid rgba(0,0,0,0.12)",
              },
            },

            muiTableBodyCellProps: {
              ...col.muiTableBodyCellProps,

              sx: {
                ...col.muiTableBodyCellProps?.sx,

                // borderLeft:
                //   index === 0 ? GROUP_BORDER : "1px solid rgba(0,0,0,0.12)",

                borderRight:
                  index === arr.length - 1
                    ? GROUP_BORDER
                    : "1px solid rgba(0,0,0,0.12)",
              },
            },
          }),
        ),
      },

      {
        id: "diff_group",
        header: "Difference",

        muiTableHeadCellProps: {
          sx: {
            borderRight: GROUP_BORDER,
            borderTop: GROUP_BORDER,
            borderBottom: GROUP_BORDER,
          },
        },
        muiTableBodyCellProps: {
          borderRight: GROUP_BORDER,
          sx: {
            borderRight: GROUP_BORDER,
          },
        },

        columns: [
          {
            id: "diff_quantity",
            header: "Qty",
            size: 30,

            muiTableBodyCellProps: {
              align: "center",

              sx: {
                borderLeft: GROUP_BORDER,

                border: "1px solid rgba(0,0,0,0.12)",
                fontWeight: 700,
              },
            },
            Cell: ({ row }) => {
              const material = row.original;
              const diff =
                material.left && material.right
                  ? Number(material.right.quantity) -
                    Number(material.left.quantity)
                  : null;

              return ![0, "0", NaN].includes(diff) ? diff : "";
            },
          },

          {
            id: "diff_length",
            header: "Dimension",
            size: 30,

            muiTableBodyCellProps: {
              align: "center",

              sx: {
                border: "1px solid rgba(0,0,0,0.12)",
                fontWeight: 700,
              },
            },

            Cell: ({ row }) => {
              const material = row.original;
              material.left &&
                material.right &&
                material.left?.description.includes("BBE, ASTM A333-6") &&
                console.log("rowwwww", material);

              const leftVal =
                material.left?.length === "-" ? 0 : material.left?.length;
              const rightVal =
                material.right?.length === "-" ? 0 : material.right?.length;

              const diff =
                material.left && material.right && Number(leftVal - rightVal)
                  ? Number(leftVal - rightVal).toFixed(1)
                  : null;

              return ![0, "0", NaN].includes(diff) ? diff : "";
            },
          },

          // {
          //   id: "diff_cost",
          //   header: "Cost",
          //   size: 30,

          //   muiTableBodyCellProps: {
          //     align: "center",

          //     sx: {
          //       border: "1px solid rgba(0,0,0,0.12)",

          //       fontWeight: 700,
          //       color: "warning.main",
          //     },
          //   },

          //   Cell: ({ row }) => {
          //     const material = row.original;
          //     const diff =
          //       material.left && material.right
          //         ? Number(material.right.cost) - Number(material.left.cost)
          //         : null;

          //     return ![0, "0", NaN].includes(diff) ? diff : "";
          //   },
          // },

          // {
          //   id: "diff_hrs",
          //   header: "Time(hrs)",
          //   size: 30,

          //   muiTableBodyCellProps: {
          //     align: "center",
          //     borderRight: GROUP_BORDER,

          //     sx: {
          //       borderRight: GROUP_BORDER,
          //       border: "1px solid rgba(0,0,0,0.12)",
          //       fontWeight: 700,
          //       color: "warning.main",
          //     },
          //   },
          // },
        ],
      },

      {
        id: "actions_group",
        header: "Actions",

        muiTableHeadCellProps: {
          sx: {
            borderRight: GROUP_BORDER,
            borderLeft: GROUP_BORDER,
            borderTop: GROUP_BORDER,
            borderBottom: GROUP_BORDER,
          },
        },

        columns: [
          {
            id: "status",
            header: "Status",
            accessorFn: (row) => row.status,
            size: 20,

            muiTableBodyCellProps: {
              align: "center",

              sx: {
                borderLeft: GROUP_BORDER,
                border: "1px solid rgba(0,0,0,0.12)",
              },
            },

            Cell: ({ row }) => {
              return (
                <FlagIcon
                  size="small"
                  color={row.original.status === "match" ? "success" : "error"}
                />
              );
            },
          },

          {
            id: "spare",
            header: "Spare",
            size: 20,

            muiTableBodyCellProps: {
              align: "center",

              sx: {
                borderLeft: GROUP_BORDER,
                border: "1px solid rgba(0,0,0,0.12)",
              },
            },

            Cell: ({ row }) => {
              return row?.original?.left?.mrqMaterials?.[0]?.spareQty;
            },
          },
          {
            id: "change_order",
            header: "Change Order",
            size: 80,

            accessorFn: (row) => {
              switch (row.status) {
                case "left-only":
                  return "Revise MRQ (CO-101)";

                case "right-only":
                  return "New PO (CO-102)";

                case "mismatch":
                  return "Update Existing";

                default:
                  return "No Action";
              }
            },

            muiTableBodyCellProps: {
              align: "center",
              sx: {
                borderRight: GROUP_BORDER,
                border: "1px solid rgba(0,0,0,0.12)",
                fontWeight: 600,
              },
            },

            Cell: ({ cell, row }) => {
              const value = () => {
                switch (row.original.status) {
                  case "left-only":
                    return "";

                  case "right-only":
                    return <Button variant="outlined">New Mrq</Button>;

                  case "mismatch":
                    return (
                      <Button
                        variant="outlined"
                        onClick={() => onClickReviseMRQ(row.original)}
                      >
                        Revise MRQ
                      </Button>
                    );

                  default:
                    return "";
                }
              };

              return (
                <Box
                  sx={{
                    fontWeight: 600,
                    color:
                      row.original.status === "match"
                        ? "success.main"
                        : row.original.status === "mismatch"
                          ? "warning.main"
                          : "error.main",
                  }}
                >
                  {value()}
                </Box>
              );
            },
          },
        ],
      },
    ],
    [leftSideLabel, rightSideLabel],
  );

  const table = useMaterialReactTable({
    columns,
    data,

    state: {
      rowSelection: selectedRows,
    },
    getRowId: (row) => row.key,
    onRowSelectionChange: setSelectedRows,

    enableRowSelection: filter === "all" ? false : true,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableHiding: false,
    enableSorting: false,
    enableStickyHeader: true,
    layoutMode: "grid",

    muiTablePaperProps: {
      variant: "outlined",
      sx: {
        borderRadius: 2,
        overflow: "hidden",
      },
    },

    muiTableContainerProps: {
      sx: {
        maxHeight: "72vh",
      },
    },

    muiTableProps: {
      sx: {
        minWidth: 1600,
      },
    },

    muiTableHeadCellProps: ({ column }) => ({
      align: "center",

      sx: {
        border: "1px solid rgba(0,0,0,0.12)",
        fontWeight: 800,
        fontSize: column.depth === 0 ? "0.82rem" : "0.74rem",
        bgcolor: column.depth === 0 ? "grey.100" : "background.paper",
        textAlign: "center",
      },
    }),

    muiTableBodyRowProps: ({ row }) => {
      const status = row.original.status;

      return {
        hover: true,

        sx: {
          bgcolor:
            status === "left-only"
              ? "rgba(230, 176, 0, 0.04)"
              : status === "right-only"
                ? "rgba(2,136,209,0.04)"
                : status === "mismatch"
                  ? "rgba(255,0,0,0.04)"
                  : status === "match"
                    ? "rgba(0,255,0,0.04)"
                    : undefined,
        },
      };
    },
  });

  if (!data.length) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        No materials match this filter.
      </Alert>
    );
  }

  return <MaterialReactTable table={table} />;
}
